import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import useGoBack from '../hooks/useGoBack'
import {
  Form,
  Input,
  InputNumber,
  Button,
  Upload,
  Select,
  Space,
  Card,
  message,
  Breadcrumb,
  Switch,
  Popconfirm
} from 'antd'
import {
  PlusOutlined,
  MinusCircleOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import {
  getProduct,
  createProduct,
  updateProduct,
  getCategories
} from '../api/store'
import { PHOTO_URL } from '../utils/apiutils'
import heic2any from 'heic2any'

const { Option } = Select

const PageContainer = styled.div`
  padding: 24px;
  background: #f0f2f5;
  min-height: 100vh;
`

const HeaderSection = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const ContentCard = styled(Card)`
  margin-bottom: 24px;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
`

const StyledForm = styled(Form)`
  max-width: 1200px;
  margin: 0 auto;
`

const ImagePreview = styled.div`
  .ant-upload-list-picture-card {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
  }

  .ant-upload-list-picture-card .ant-upload-list-item {
    margin: 0 !important;
  }

  .ant-upload.ant-upload-select-picture-card {
    margin: 0 !important;
  }
`

const ButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
  padding: 24px 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
`

const PriceGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 24px;
`

const StatusSwitch = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
  padding: 16px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
`

const StatusText = styled.span`
  color: ${(props) => (props.isActive ? '#52c41a' : '#ff4d4f')};
  font-weight: 500;
`

// 添加新的樣式組件
const SpecificationItem = styled(Space)`
  width: 100%;
  margin-bottom: 16px !important;

  @media (max-width: 768px) {
    flex-direction: column !important;
    align-items: flex-start !important;

    .spec-name {
      width: 100% !important;
      margin-bottom: 8px;
    }

    .spec-options {
      width: 100% !important;
    }

    .delete-icon {
      margin-top: 8px;
    }
  }
`

const AddProductPage = () => {
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const location = useLocation()
  // 沒有站內歷史時（重整、直接貼網址）退回進來時的那個列表狀態，含分類 tab
  const backTo = location.state?.from || '/store'
  const goBack = useGoBack(backTo)
  const { productId } = useParams()
  const [description, setDescription] = useState('')
  const [loading, setLoading] = useState(false)
  const [previewImage, setPreviewImage] = useState('')
  const [previewOpen, setPreviewOpen] = useState(false)
  const [isActive, setIsActive] = useState(true)
  const [initialData, setInitialData] = useState(null)
  const [hasChanges, setHasChanges] = useState(false)
  const [categories, setCategories] = useState([])
  const [deletedImages, setDeletedImages] = useState([]) // 跟踪已删除的图片 URL

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await getCategories()
        setCategories(res.data.data)
      } catch (error) {
        message.error('獲取分類失敗')
      }
    }
    fetchCategories()
  }, [])

  useEffect(() => {
    if (productId) {
      const fetchProduct = async () => {
        try {
          const res = await getProduct(productId)
          const product = res.data.data
          console.log('product', product)
          setInitialData(product)
          setIsActive(product.status === 'active')
          form.setFieldsValue({
            title: product.title,
            category: product.category.id,
            originalPrice: product.originalPrice,
            price: product.price,
            cost: product.cost,
            stock: product.stock,
            specifications: product.specifications.map((spec) => ({
              name: spec.name,
              options: spec.options
            })),
            images: product.images.map((url, index) => {
              const imageObj = {
                uid: `-${index}`,
                name: `image-${index}`,
                status: 'done',
                url: `${PHOTO_URL}${url}`,
                originalUrl: url // 保存原始 URL 用于删除
              }
              console.log(`圖片 ${index} 設置:`, imageObj)
              return imageObj
            }),
            description: product.description,
            tag: product.tag
          })
          setDescription(product.description)
        } catch (error) {
          console.error('Error fetching product:', error)
          message.error('獲取商品數據失敗')
        }
      }
      fetchProduct()
    }
  }, [productId, form])

  // 監聽表單變化
  const handleFormChange = () => {
    if (initialData) {
      const currentValues = form.getFieldsValue()
      const hasChanged =
        JSON.stringify(currentValues) !== JSON.stringify(initialData)
      setHasChanges(hasChanged)
    }
  }

  const handleFinish = async (values) => {
    setLoading(true)
    try {
      const formData = new FormData()

      // 基本資料
      formData.append('title', values.title)
      formData.append('name', values.title) // name 通常與 title 相同
      formData.append('categoryId', values.category)
      formData.append('price', values.price)
      formData.append('suggestedPrice', values.price) // suggestedPrice 通常與 price 相同
      formData.append('originalPrice', values.originalPrice)
      formData.append('cost', values.cost)
      formData.append('stock', values.stock)
      formData.append('description', description)
      formData.append('status', isActive ? 'active' : 'inactive')

      // 處理 specifications 格式
      const specifications = values.specifications
        .filter((spec) => spec.name && spec.options && spec.options.length > 0)
        .map((spec) => ({
          name: spec.name,
          options: Array.isArray(spec.options) ? spec.options : []
        }))

      // 將處理後的 specifications 轉為 JSON 字符串
      formData.append('specifications', JSON.stringify(specifications))

      // 處理圖片
      if (productId) {
        // 編輯模式：處理圖片刪除
        console.log('刪除的圖片列表:', deletedImages)
        if (deletedImages.length > 0) {
          // 將刪除的圖片 URL 添加到 deleteImages 參數（JSON 字符串格式）
          const deleteImagesJson = JSON.stringify(deletedImages)
          console.log('添加 deleteImages:', deleteImagesJson)
          formData.append('deleteImages', deleteImagesJson)
        }

        // 編輯模式：處理新圖片上傳（使用 files 字段）
        if (values.images && values.images.length > 0) {
          let newImageCount = 0
          values.images.forEach((image) => {
            // 只上傳新選擇的圖片（有 originFileObj 的）
            if (image.originFileObj) {
              console.log('添加新圖片:', image.name)
              formData.append('files', image.originFileObj)
              newImageCount++
            }
          })
          console.log('新上傳的圖片數量:', newImageCount)
        }
      } else {
        // 新增模式：使用 images 字段上傳圖片
        if (values.images && values.images.length > 0) {
          values.images.forEach((image) => {
            if (image.originFileObj) {
              formData.append('images', image.originFileObj)
            }
          })
        }
      }

      if (values.tag) {
        formData.append('tag', values.tag)
      }

      if (productId) {
        // 編輯模式：重置刪除列表（提交後清空）
        await updateProduct(productId, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        message.success('商品更新成功')
        setDeletedImages([]) // 清空刪除列表
      } else {
        await createProduct(formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        message.success('商品新增成功')
      }
      navigate(backTo)
    } catch (error) {
      console.error('Error:', error)
      message.error(productId ? '更新失敗' : '新增失敗')
    }
    setLoading(false)
  }

  // 處理商品狀態變更
  const handleStatusChange = (checked) => {
    setIsActive(checked)
    setHasChanges(true)
  }

  // 處理離開頁面前的確認
  const handleLeave = () => {
    if (hasChanges) {
      return window.confirm('您有未保存的更改，確定要離開嗎？')
    }
    return true
  }

  // 富文本編輯器配置
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ color: [] }, { background: [] }],
      ['link'],
      ['clean']
    ]
  }

  const handleDescriptionChange = (content) => {
    console.log('HTML content:', content)
    setDescription(content)
  }

  // 修改 Upload 組件配置
  const uploadProps = {
    beforeUpload: async (file) => {
      const isAllowedType = /\.(jpeg|jpg|png|gif|heic|heif)$/i.test(file.name)
      const isLt10M = file.size / 1024 / 1024 < 10

      if (!isLt10M) {
        message.error('圖片必須小於 10MB！')
        return Upload.LIST_IGNORE
      }

      // 檢查當前圖片數量
      const currentImages = form.getFieldValue('images') || []
      if (currentImages.length >= 10) {
        message.error('最多只能上傳 10 張圖片')
        return Upload.LIST_IGNORE
      }

      // 如果是 HEIC/HEIF 格式，進行轉換
      if (/\.(heic|heif)$/i.test(file.name)) {
        try {
          setLoading(true)
          const blob = await heic2any({
            blob: file,
            toType: 'image/jpeg',
            quality: 0.8
          })

          const newFile = new File(
            [blob],
            file.name.replace(/\.(heic|heif)$/i, '.jpg'),
            {
              type: 'image/jpeg'
            }
          )

          message.success('圖片轉換成功')
          setLoading(false)
          return newFile
        } catch (error) {
          setLoading(false)
          message.error('圖片轉換失敗，請使用其他格式的圖片')
          console.error('HEIC 轉換錯誤：', error)
          return Upload.LIST_IGNORE
        }
      }

      if (!isAllowedType) {
        message.error('只能上傳 JPG/PNG/GIF/HEIC/HEIF 格式的圖片！')
        return Upload.LIST_IGNORE
      }

      return false
    },
    multiple: true,
    maxCount: 10,
    listType: 'picture-card',
    accept: 'image/*',
    customRequest: ({ file, onSuccess }) => {
      setTimeout(() => {
        onSuccess('ok')
      }, 0)
    }
  }

  // 修改 Upload 組件的 onChange 處理
  const handleUploadChange = ({ fileList: newFileList, file }) => {
    // 處理文件列表
    const updatedFileList = newFileList.map((file) => {
      // 如果文件是通過 beforeUpload 轉換的，更新其 type
      if (file.originFileObj && /\.(heic|heif)$/i.test(file.name)) {
        return {
          ...file,
          type: 'image/jpeg',
          name: file.name.replace(/\.(heic|heif)$/i, '.jpg')
        }
      }
      return file
    })

    // 限制最多 10 張圖片
    const limitedFileList = updatedFileList.slice(0, 10)
    form.setFieldsValue({ images: limitedFileList })
    setHasChanges(true)
  }

  // 處理圖片刪除
  const handleImageRemove = (file) => {
    console.log('刪除圖片:', file)
    console.log('原始 URL:', file.originalUrl)

    // 如果是已存在的圖片（有 originalUrl），添加到刪除列表
    if (file.originalUrl) {
      setDeletedImages((prev) => {
        // 避免重複添加
        if (!prev.includes(file.originalUrl)) {
          const newList = [...prev, file.originalUrl]
          console.log('更新刪除列表:', newList)
          return newList
        }
        return prev
      })
    } else {
      console.warn('刪除的圖片沒有 originalUrl，可能是新上傳的圖片')
    }
    setHasChanges(true)
    return true // 返回 true 允許刪除
  }

  return (
    <PageContainer>
      <HeaderSection>
        <Breadcrumb
          items={[
            { title: <a onClick={() => navigate('/store')}>商品管理</a> },
            { title: productId ? '編輯商品' : '新增商品' }
          ]}
        />
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => {
            if (handleLeave()) {
              goBack()
            }
          }}
        >
          返回列表
        </Button>
      </HeaderSection>

      {productId && (
        <StatusSwitch>
          <span>商品狀態：</span>
          <Switch
            checked={isActive}
            onChange={handleStatusChange}
            checkedChildren='上架中'
            unCheckedChildren='已下架'
          />
          <StatusText isActive={isActive}>
            {isActive ? '商品已上架' : '商品已下架'}
          </StatusText>
        </StatusSwitch>
      )}

      <StyledForm
        form={form}
        layout='vertical'
        onFinish={handleFinish}
        onValuesChange={handleFormChange}
        initialValues={{
          specifications: [{ name: '', options: [] }]
        }}
      >
        <ContentCard title='基本信息'>
          <Form.Item
            name='title'
            label='商品名稱'
            rules={[{ required: true, message: '請輸入商品名稱' }]}
          >
            <Input placeholder='請輸入商品名稱' />
          </Form.Item>

          <Form.Item
            name='category'
            label='商品分類'
            rules={[{ required: true, message: '請選擇商品分類' }]}
          >
            <Select placeholder='請選擇分類'>
              {categories?.map((category) => (
                <Option key={category._id} value={category.id}>
                  {category.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <ImagePreview>
            <Form.Item
              name='images'
              label='商品圖片'
              valuePropName='fileList'
              getValueFromEvent={(e) => {
                if (Array.isArray(e)) {
                  return e
                }
                return e && e.fileList
              }}
              rules={[{ required: true, message: '請上傳商品圖片' }]}
              extra='支持多張圖片上傳，最多10張，每張大小不超過10MB'
            >
              <Upload
                {...uploadProps}
                onChange={handleUploadChange}
                onRemove={handleImageRemove}
              >
                {(!form.getFieldValue('images') ||
                  form.getFieldValue('images').length < 10) && (
                  <div>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>上傳</div>
                  </div>
                )}
              </Upload>
            </Form.Item>
          </ImagePreview>

          <Form.Item name='tag' label='商品標籤' tooltip='請輸入一個標籤'>
            <Input placeholder='請輸入商品標籤' />
          </Form.Item>
        </ContentCard>

        <ContentCard title='價格與庫存'>
          <PriceGroup>
            <Form.Item
              name='originalPrice'
              label='原價'
              rules={[{ required: true, message: '請輸入原價' }]}
            >
              <InputNumber
                min={0}
                precision={0}
                prefix='NT$'
                style={{ width: '100%' }}
                placeholder='請輸入原價'
              />
            </Form.Item>

            <Form.Item
              name='price'
              label='特價'
              rules={[{ required: true, message: '請輸入特價' }]}
            >
              <InputNumber
                min={0}
                precision={0}
                prefix='NT$'
                style={{ width: '100%' }}
                placeholder='請輸入特價'
              />
            </Form.Item>

            <Form.Item name='cost' label='成本'>
              {/* <span>為您計算銷售報表，了解營運情況</span> */}
              <InputNumber
                min={0}
                precision={0}
                prefix='NT$'
                style={{ width: '100%' }}
                placeholder='請輸入成本'
              />
            </Form.Item>

            <Form.Item
              name='stock'
              label='庫存數量'
              rules={[{ required: true, message: '輸入庫存數量' }]}
            >
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                placeholder='請輸入庫存數量'
              />
            </Form.Item>
          </PriceGroup>
        </ContentCard>

        <ContentCard title='規格設置'>
          <Form.List name='specifications'>
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <SpecificationItem key={key} align='baseline'>
                    <Form.Item
                      {...restField}
                      name={[name, 'name']}
                      rules={[{ required: true, message: '請輸入規格名稱' }]}
                      className='spec-name'
                    >
                      <Input
                        placeholder='規格名稱（如：顏色）'
                        style={{ width: '200px' }}
                      />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'options']}
                      rules={[{ required: true, message: '請添加規格選項' }]}
                      className='spec-options'
                    >
                      <Select
                        mode='tags'
                        style={{ width: '400px' }}
                        placeholder='輸入選項後按Enter（如：紅色）'
                      />
                    </Form.Item>
                    {fields.length > 1 && (
                      <MinusCircleOutlined
                        onClick={() => remove(name)}
                        className='delete-icon'
                      />
                    )}
                  </SpecificationItem>
                ))}
                <Form.Item>
                  <Button
                    type='dashed'
                    onClick={() => add()}
                    icon={<PlusOutlined />}
                    style={{ width: '200px' }}
                  >
                    添加規格
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </ContentCard>

        <ContentCard title='商品描述'>
          <Form.Item
            name='description'
            label='商品描述'
            rules={[{ required: true, message: '請輸入商品描述' }]}
          >
            <ReactQuill
              value={description}
              onChange={handleDescriptionChange}
              modules={quillModules}
              style={{ height: '400px', marginBottom: '50px' }}
            />
          </Form.Item>
        </ContentCard>

        <ButtonGroup>
          <Popconfirm
            title='確定要取消嗎？'
            description='取消後所有更改都將丟失'
            onConfirm={() => navigate(backTo)}
            okText='確定'
            cancelText='取消'
            disabled={!hasChanges}
          >
            <Button size='large' disabled={!hasChanges}>
              取消
            </Button>
          </Popconfirm>
          <Button
            type='primary'
            size='large'
            htmlType='submit'
            loading={loading}
            disabled={!hasChanges && productId}
          >
            {productId ? '保存修改' : '確認新增'}
          </Button>
        </ButtonGroup>
      </StyledForm>
    </PageContainer>
  )
}

export default AddProductPage
